// deployment_class: customer_env_allowed
/**
 * SurfaceHub — renders a zone of the CSA surface registry (Settings & Account
 * IA spec 2026-06-24 §3 + §4.2). The hub is a PROJECTION of the registry:
 * nav entries, routes, scope-gating, and the rendered panel are all derived
 * from `visibleSurfaces(zone, scopes)` / `getSurfaces(zone)` — never a
 * hand-maintained table. Register a `CsaSurfaceDeclaration` and its nav entry +
 * route + gate appear here automatically.
 *
 * Routing: mounted under a `path="<basePath>/*"` route; it renders a nested
 * `<Routes>` with one child per registered surface (`<basePath>/<id>`). The
 * index redirects to the first surface the actor may see. A gated surface is
 * wrapped in the shipped <RequireScope> (defence-in-depth: the nav already
 * hides it, but the route guards too — never trust the nav alone).
 */
import { Suspense, lazy, useMemo } from 'react';
import type { LazyExoticComponent, ComponentType } from 'react';
import { Routes, Route, Navigate, NavLink } from 'react-router-dom';
import type { SurfaceZone, CsaSurfaceDeclaration } from './types';
import { getSurfaces, visibleSurfaces } from './registry';
import { useAuth } from '../auth/useAuth';
import { RequireScope } from '../auth/RequireScope';

/** Cache lazy components by surface id so they don't remount each render. */
const lazyCache = new Map<string, LazyExoticComponent<ComponentType>>();
function lazyFor(s: CsaSurfaceDeclaration): LazyExoticComponent<ComponentType> {
  let c = lazyCache.get(s.id);
  if (!c) {
    c = lazy(s.component);
    lazyCache.set(s.id, c);
  }
  return c;
}

function SurfacePanel({ surface }: { surface: CsaSurfaceDeclaration }): JSX.Element {
  const Lazy = lazyFor(surface);
  const panel = (
    <Suspense fallback={<div className="p-page" aria-busy="true" />}>
      <Lazy />
    </Suspense>
  );
  // Defence-in-depth: gate the route too, even though the nav already hides it.
  return surface.requiredScope ? (
    <RequireScope scope={surface.requiredScope}>{panel}</RequireScope>
  ) : (
    panel
  );
}

export function SurfaceHub({
  zone,
  basePath,
  title,
}: {
  zone: SurfaceZone;
  basePath: string;
  title: string;
}): JSX.Element {
  const { scopes } = useAuth();
  // ALL surfaces define routes (each route self-guards); the NAV shows only the
  // ones the actor may see. Empty nav is intentional (near-empty auditor case).
  const all = useMemo(() => getSurfaces(zone), [zone]);
  const visible = useMemo(() => visibleSurfaces(zone, scopes), [zone, scopes]);
  const first = visible[0];

  // The personal account zone (Profile / Preferences) wears the user's chosen
  // dashboard backdrop behind frosted-glass panels, matching the dashboards.
  // Other zones keep the flat paper canvas.
  const isAccountZone = zone === 'settings.personal';

  return (
    <div className={`flex flex-col md:flex-row min-h-screen ${isAccountZone ? 'app-backdrop' : 'bg-paper'}`}>
      <nav
        aria-label={`${title} sections`}
        // Mobile: full-width, stacked on top (border-b). Desktop (md+): the fixed
        // 240px side rail (border-r). Without the responsive stack the content
        // column overflowed a phone viewport and clipped off-screen (mobile bug).
        className={`w-full md:w-60 shrink-0 border-b md:border-b-0 md:border-r border-rule p-4 ${
          isAccountZone
            ? 'bg-white/50 backdrop-blur-lg border-white/50'
            : 'bg-paper-soft'
        }`}
      >
        <p className="mb-3 font-body text-label uppercase tracking-wide text-warm-gray-d">
          {title}
        </p>
        <ul className="grid gap-1">
          {visible.map((s) => (
            <li key={s.id}>
              <NavLink
                to={`${basePath}/${s.id}`}
                className={({ isActive }) =>
                  `block rounded px-3 py-2 text-body-s ${
                    isActive ? 'bg-paper text-ink font-medium' : 'text-warm-gray-d hover:text-ink'
                  }`
                }
              >
                {s.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="min-w-0 flex-1">
        <Routes>
          <Route
            index
            element={
              first ? (
                <Navigate to={`${basePath}/${first.id}`} replace />
              ) : (
                <div className="p-page text-body-s text-warm-gray-d" role="status">
                  No settings are available for your role.
                </div>
              )
            }
          />
          {all.map((s) => (
            <Route key={s.id} path={s.id} element={<SurfacePanel surface={s} />} />
          ))}
        </Routes>
      </div>
    </div>
  );
}
